import { motion } from 'framer-motion';
import { CodeBracketIcon, DevicePhoneMobileIcon, PaintBrushIcon, ArrowRightIcon, CheckIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import AnimatedSection from '../components/AnimatedSection';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: CodeBracketIcon,
      title: 'Website Development',
      subtitle: 'Modern, responsive, and lightning-fast websites',
      description: 'We create stunning websites that not only look amazing but perform exceptionally across all devices. Our development process focuses on user experience, performance optimization, and modern web standards.',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085',
      features: [
        'Responsive Design for All Devices',
        'SEO Optimization & Performance',
        'Custom CMS Integration',
        'E-commerce Solutions',
        'Progressive Web Apps',
        'Advanced Analytics Setup'
      ],
      technologies: ['React', 'Next.js', 'TailwindCSS', 'Node.js', 'MongoDB', 'AWS'],
      startingPrice: '$2,999'
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Application Development',
      subtitle: 'Cross-platform applications that scale',
      description: 'From concept to deployment, we build robust applications that work seamlessly across web and mobile platforms. Our apps are designed for performance, scalability, and exceptional user engagement.',
      image: 'https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg',
      features: [
        'Cross-Platform Development',
        'Native iOS & Android Apps',
        'Real-time Data Synchronization',
        'Push Notifications & Analytics',
        'Offline Functionality',
        'API Integration & Development'
      ],
      technologies: ['React Native', 'Flutter', 'Firebase', 'GraphQL', 'TypeScript', 'Docker'],
      startingPrice: '$4,999'
    },
    {
      icon: ComputerDesktopIcon,
      title: 'Software Development',
      subtitle: 'Custom software solutions tailored to your business needs',
      description: 'From MVP to enterprise-grade systems, we design and develop scalable software that solves complex problems, automates workflows, and drives efficiency—whether for desktop, cloud, or embedded environments. Our solutions are tailored to meet your specific business needs.',
      image: 'https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg',
      features: [
        'Custom Desktop & Cloud Solutions (Windows, macOS, Linux)',
        'Database Design & Optimization (SQL, NoSQL)',
        'API & Microservices Architecture',
        'User Authentication & Role Management',
        'Third-Party Integrations (Payment gateways, CRM, etc.)',
        'Automated Testing & CI/CD Pipelines'
      ],
      technologies: ['Python', 'Java', '.NET', 'Docker', 'Node.js', 'Kubernetes', 'AWS/GCP', 'PostgreSQL', 'REST/GraphQL'],
      startingPrice: '$5,999'
    },
    {
      icon: PaintBrushIcon,
      title: 'Graphic Design',
      subtitle: 'Visual identity that speaks volumes',
      description: 'Our design team creates compelling visual experiences that communicate your brand message effectively. From logos to complete brand systems, we ensure your visual identity stands out.',
      image: 'https://images.pexels.com/photos/326514/pexels-photo-326514.jpeg',
      features: [
        'Brand Identity & Logo Design',
        'UI/UX Design Systems',
        'Marketing Materials',
        'Social Media Graphics',
        'Print & Digital Collateral',
        'Motion Graphics & Animation'
      ],
      technologies: ['Figma', 'Adobe Creative Suite', 'Sketch', 'Principle', 'After Effects', 'Blender'],
      startingPrice: '$1,999'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Strategy',
      description: 'We start by understanding your business goals, target audience, and technical requirements to create a comprehensive project roadmap.'
    },
    {
      step: '02',
      title: 'Design & Prototyping',
      description: 'Our design team creates detailed mockups and interactive prototypes that visualize the final product before development begins.'
    },
    {
      step: '03',
      title: 'Development & Testing',
      description: 'Using cutting-edge technologies, we build your solution with rigorous testing at every stage to ensure quality and performance.'
    },
    {
      step: '04',
      title: 'Launch & Support',
      description: 'We handle the deployment process and provide ongoing support to ensure your solution continues to perform optimally.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-30" />
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-cleantech-white mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 max-w-4xl mx-auto">
              Comprehensive digital solutions designed to elevate your brand and drive business growth through innovative technology and exceptional design.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="section-container">
          <div className="space-y-20">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <AnimatedSection key={service.title} delay={index * 0.2}>
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-col-dense' : ''}`}>
                    {/* Service Image */}
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className={`relative ${!isEven ? 'lg:col-start-2' : ''}`}
                    >
                      <div className="relative overflow-hidden rounded-2xl shadow-premium">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-96 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-cleantech-charcoal/80 to-transparent" />
                        <div className="absolute bottom-6 left-6 flex items-center space-x-3">
                          <div className="w-16 h-16 bg-gold-gradient rounded-xl flex items-center justify-center">
                            <IconComponent className="w-8 h-8 text-cleantech-charcoal" />
                          </div>
                          <div>
                            <div className="text-cleantech-white font-bold text-lg">{service.title}</div>
                            <div className="text-cleantech-gold">{service.subtitle}</div>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Service Content */}
                    <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
                      <div className="glass-card p-8">
                        <h3 className="text-3xl lg:text-4xl font-bold text-cleantech-white mb-4">
                          {service.title}
                        </h3>
                        <p className="text-lg text-white/80 mb-6">
                          {service.description}
                        </p>

                        {/* Features */}
                        <div className="mb-8">
                          <h4 className="text-xl font-semibold text-cleantech-white mb-4">What's Included:</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {service.features.map((feature, featureIndex) => (
                              <div key={featureIndex} className="flex items-center space-x-3">
                                <CheckIcon className="w-5 h-5 text-cleantech-gold flex-shrink-0" />
                                <span className="text-white/80">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Technologies */}
                        <div className="mb-8">
                          <h4 className="text-xl font-semibold text-cleantech-white mb-4">Technologies:</h4>
                          <div className="flex flex-wrap gap-2">
                            {service.technologies.map((tech, techIndex) => (
                              <span
                                key={techIndex}
                                className="px-3 py-1 bg-cleantech-gold/20 text-cleantech-gold rounded-lg text-sm"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Pricing and CTA */}
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-white/60">Starting from</div>
                            <div className="text-3xl font-bold text-gradient">{service.startingPrice}</div>
                          </div>
                          <Link to="/contact" className="btn-primary group">
                            Get Quote
                            <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <AnimatedSection className="py-20 bg-cleantech-charcoal-light">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
              Our <span className="text-gradient">Process</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              We follow a proven methodology that ensures project success from conception to launch and beyond.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="glass-card p-6 text-center hover:shadow-gold transition-all duration-500"
              >
                <div className="w-16 h-16 bg-gold-gradient rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl text-cleantech-charcoal">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-cleantech-white mb-3">{step.title}</h3>
                <p className="text-white/80">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-20">
        <div className="section-container">
          <div className="glass-card p-12 text-center hover:shadow-gold-lg transition-all duration-500">
            <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
              Ready to Start Your <span className="text-gradient">Project?</span>
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
              Let's discuss your requirements and create a custom solution that exceeds your expectations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Get Free Consultation
              </Link>
              <Link to="/portfolio" className="btn-secondary">
                View Our Portfolio
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default Services;